<template>
  <v-container class="pt-0 pb-5">
    <v-row>
      <v-col cols="12">
        <div class="pa-2 rounded elevation-1 bg-white">
          <SportipsButton backButton chevronLeft>Retour</SportipsButton>

          <div class="text-h5 font-weight-bold my-2">Récapitulatif plan d'entraînement</div>
          <div class="text-subtitle-1 mb-4">{{ mainStore.module.nom }}</div>

          <v-alert v-if="error" type="error" variant="tonal" class="mb-4">{{ error }}</v-alert>

          <div v-if="loading" class="d-flex justify-center my-8">
            <v-progress-circular indeterminate color="primary-dark"></v-progress-circular>
          </div>

          <template v-else-if="data">
            <v-card class="pa-4 mb-4" variant="outlined">
              <div class="text-h6 mb-3">Configuration</div>
              <v-row dense>
                <v-col cols="12" md="6">
                  <div><span class="font-weight-bold">Titre :</span> {{ data.plan_entrainement.titre }}</div>
                </v-col>
                <v-col cols="12" md="3">
                  <div><span class="font-weight-bold">Prix :</span> {{ data.plan_entrainement.prix }}€</div>
                </v-col>
                <v-col cols="12" md="3">
                  <div>
                    <span class="font-weight-bold">Actif :</span>
                    <v-chip size="small" :color="data.plan_entrainement.actif ? 'green-darken-4' : 'red-darken-4'"
                      class="ml-1">
                      {{ data.plan_entrainement.actif ? 'Oui' : 'Non' }}
                    </v-chip>
                  </div>
                </v-col>
                <v-col cols="12" v-if="data.plan_entrainement.description">
                  <div><span class="font-weight-bold">Description :</span> {{ data.plan_entrainement.description }}
                  </div>
                </v-col>
              </v-row>
            </v-card>

            <v-card class="pa-4 mb-4" variant="outlined">
              <div class="text-h6 mb-3">Utilisation par course</div>
              <v-table density="compact">
                <thead>
                  <tr>
                    <th>Course</th>
                    <th class="text-right">Codes total</th>
                    <th class="text-right">Utilisés</th>
                    <th class="text-right">Disponibles</th>
                    <th class="text-right">Plans vendus</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="stat in data.stats_par_course" :key="stat.id_course">
                    <td>{{ stat.nom_course || 'Course #' + stat.id_course }}</td>
                    <td class="text-right">{{ stat.nb_codes_total }}</td>
                    <td class="text-right">{{ stat.nb_codes_utilises }}</td>
                    <td class="text-right">{{ stat.nb_codes_disponibles }}</td>
                    <td class="text-right">{{ stat.nb_plans_vendus }}</td>
                  </tr>
                  <tr v-if="!data.stats_par_course.length">
                    <td colspan="5" class="text-center text-medium-emphasis">Aucune statistique disponible</td>
                  </tr>
                </tbody>
                <tfoot v-if="data.stats_par_course.length">
                  <tr class="font-weight-bold">
                    <td>Total</td>
                    <td class="text-right">{{ totals.nb_codes_total }}</td>
                    <td class="text-right">{{ totals.nb_codes_utilises }}</td>
                    <td class="text-right">{{ totals.nb_codes_disponibles }}</td>
                    <td class="text-right">{{ totals.nb_plans_vendus }}</td>
                  </tr>
                </tfoot>
              </v-table>
            </v-card>

            <v-card class="pa-4" variant="outlined">
              <div class="d-flex align-center justify-space-between mb-3">
                <div class="text-h6">Liste des codes ({{ data.codes.length }})</div>
                <v-chip size="small" color="primary-dark" variant="tonal">
                  {{ totals.nb_codes_disponibles }} disponibles
                </v-chip>
              </div>
              <v-table density="compact">
                <thead>
                  <tr>
                    <th>Course</th>
                    <th>Nom</th>
                    <th>Code</th>
                    <th>Réduction</th>
                    <th>Validité</th>
                    <th>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="code in data.codes" :key="code.id">
                    <td>{{ code.nom_course || 'Course #' + code.id_course }}</td>
                    <td>{{ code.nom }}</td>
                    <td><code>{{ code.code }}</code></td>
                    <td>{{ code.reduction || '-' }}</td>
                    <td>{{ code.validity || '-' }}</td>
                    <td>
                      <v-chip size="x-small" :color="code.used == 1 ? 'red-darken-4' : 'green-darken-4'">
                        {{ code.used == 1 ? 'Utilisé' : 'Disponible' }}
                      </v-chip>
                    </td>
                  </tr>
                  <tr v-if="!data.codes.length">
                    <td colspan="6" class="text-center text-medium-emphasis">Aucun code enregistré</td>
                  </tr>
                </tbody>
              </v-table>
            </v-card>
          </template>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapStores } from "pinia";
import { useMainStore } from "@/stores/mainStore";
import UserService from "@/services/user.service";

const ALLOWED_USER_IDS = [1, 175144];

export default {
  data() {
    return {
      loading: false,
      error: null,
      data: null,
    };
  },
  computed: {
    ...mapStores(useMainStore),
    totals() {
      if (!this.data?.stats_par_course?.length) {
        return {
          nb_codes_total: 0,
          nb_codes_utilises: 0,
          nb_codes_disponibles: 0,
          nb_plans_vendus: 0,
        };
      }
      return this.data.stats_par_course.reduce(
        (acc, stat) => ({
          nb_codes_total: acc.nb_codes_total + stat.nb_codes_total,
          nb_codes_utilises: acc.nb_codes_utilises + stat.nb_codes_utilises,
          nb_codes_disponibles: acc.nb_codes_disponibles + stat.nb_codes_disponibles,
          nb_plans_vendus: acc.nb_plans_vendus + stat.nb_plans_vendus,
        }),
        {
          nb_codes_total: 0,
          nb_codes_utilises: 0,
          nb_codes_disponibles: 0,
          nb_plans_vendus: 0,
        }
      );
    },
  },
  methods: {
    hasAccess() {
      const userId = parseInt(this.mainStore.authUser?.userData?.id, 10);
      return this.mainStore.authUser?.isLogged && ALLOWED_USER_IDS.includes(userId);

    },
    async loadData() {
      this.loading = true;
      this.error = null;
      const res = await UserService.getPlanEntrainementRecap(this.mainStore.module.id);
      this.loading = false;
      if (!res) {
        this.error = "Impossible de charger les données du plan d'entraînement.";
        return;
      }
      this.data = res;
    },
  },
  mounted() {
    if (!this.hasAccess()) {
      this.$router.push({ name: "Home", params: { base_module: this.$route.params.base_module } });
      return;
    }
    this.loadData();
  },
};
</script>
